import { useRef } from "react";
import PropTypes from "prop-types";
import {
  CenterModal, ModalSize, Button, ButtonVariant,
} from "@repo/design-system/components/ui";
import { FormBuilder, FormFieldTypes, validationTypes } from "@repo/design-system/components/forms";
import { getValue } from "@repo/design-system/components/forms/utils";
import {
  useGetStudentDropdownQuery, useLazyRegisterAcademicRecordQuery,
} from "@gyandiksha/shared-ui/app/services";
import { ToastService } from "@/services";

const buildEducationTypeFormConfig = (options) => ({
  name: "Add Academic Record",
  initialState: { educationType: "" },
  section: [
    {
      title: "",
      style: { sectionStyle: "" },
      rowGroup: [
        {
          style: { groupStyle: "row" },
          fields: [
            {
              id: "educationType",
              label: "Select Education Type",
              Field_Name: "Select Education Type",
              fieldType: FormFieldTypes.SELECT,
              dataField: "educationType",
              fieldSetting: { placeholder: "Select education type", options, isSearchable: false },
              validation: [{ type: validationTypes.REQUIRE }],
              style: { containerCss: "mb-0" },
            },
          ],
        },
      ],
    },
  ],
});

// First step of the "add record" flow — lets the student pick which education type to register,
// then hands the resulting draft record (mutable/immutable) up so the parent can open the detail modal.
const AddAcademicRecordModal = ({ isOpen, onClose, onContinue }) => {
  const formRef = useRef();

  const { data: dropdownData } = useGetStudentDropdownQuery(undefined, { skip: !isOpen });
  const qualificationList = dropdownData?.data?.qualification?.content ?? [];
  const qualificationOptions = qualificationList.map((q) => ({ value: String(q.id), label: q.value }));

  const [registerAcademicRecord, { isFetching: isRegistering }] = useLazyRegisterAcademicRecordQuery();

  const handleContinue = async () => {
    const formData = formRef.current?.getValidatedFormData();
    if (!formData) return;

    const selectedId = Number(getValue(formData.educationType));
    const selectedLabel = qualificationOptions.find((option) => option.value === String(selectedId))?.label;

    const result = await registerAcademicRecord({ programLevelId: selectedId });
    const payload = result?.data?.data;
    const type = payload ? Object.keys(payload)[0] : null;

    if (type) {
      onContinue({ type, record: payload[type], programLevelId: selectedId, title: selectedLabel });
    } else {
      ToastService.error({ description: result?.error?.message || "Unable to fetch academic record details." });
    }
  };

  return (
    <CenterModal
      isOpen={isOpen}
      handleToggleModal={onClose}
      modalTitle="Add Academic Record"
      modelSizeClass={ModalSize.WD50}
      footerChildren={(
        <>
          <Button variant={ButtonVariant.OUTLINE} leftIcon="material-symbols:close-rounded" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            leftIcon="material-symbols:arrow-forward-rounded"
            onClick={handleContinue}
            loading={isRegistering}
          >
            Save &amp; Continue
          </Button>
        </>
      )}
    >
      <FormBuilder ref={formRef} config={buildEducationTypeFormConfig(qualificationOptions)} />
    </CenterModal>
  );
};

AddAcademicRecordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

export default AddAcademicRecordModal;
